import { useState, useEffect } from "react";
import './Login.css';
import logo from '../../assets/Artfro-Logo1.png';
import { useNavigate } from 'react-router-dom';
import API_ENDPOINTS from '../../config/apiConfig';
import { fetchFromApi } from '../../config/api';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';

const features = [
    { icon: 'fa-solid fa-users',             text: 'Manage registered users & artists' },
    { icon: 'fa-solid fa-bag-shopping',      text: 'Track orders & donations in real-time' },
    { icon: 'fa-solid fa-layer-group',       text: 'Curate categories & product catalog' },
    { icon: 'fa-solid fa-headset',           text: 'Handle contact support tickets' },
];

function Login() {
    const [userDetail, setUserDetail] = useState({ email: "", password: "" });
    const [emailError, setEmailError] = useState(null);
    const [passwordError, setPasswordError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [apiError, setApiError] = useState(null);

    const { isAuthenticated, login } = useAuth();
    const { fetchDataAfterLogin } = useData();
    const navigate = useNavigate();

    const onValueChange = (e) => {
        setUserDetail({ ...userDetail, [e.target.name]: e.target.value });
        if (e.target.name === "email") setEmailError(null);
        if (e.target.name === "password") setPasswordError(null);
        setApiError(null);
    };

    const isValidEmail = (email) => /\S+@\S+\.\S+/.test(email);

    useEffect(() => {
        if (isAuthenticated) navigate('/dashboard');
    }, [isAuthenticated, navigate]);

    const handleLoginSubmit = async (e) => {
        e.preventDefault();
        let valid = true;
        if (!isValidEmail(userDetail.email)) {
            setEmailError("Please enter a valid email address.");
            valid = false;
        }
        if (userDetail.password.length === 0) {
            setPasswordError("Please enter your password.");
            valid = false;
        }
        if (!valid) return;

        setLoading(true);
        try {
            const data = await fetchFromApi(API_ENDPOINTS.LOGIN, 'POST', {
                email: userDetail.email,
                password: userDetail.password
            });
            if (data.status === "success") {
                login(data.token, JSON.stringify(data.user));
                await fetchDataAfterLogin();
                navigate('/dashboard');
            } else {
                if (data.errorType === "email") setEmailError(data.message);
                else setPasswordError(data.message);
                setApiError(data.message);
            }
        } catch (err) {
            setApiError("An error occurred. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-page">
            {/* Left panel */}
            <div className="login-left">
                <div className="login-left-content">
                    <img src={logo} alt="Artfro Logo" />
                    <p className="login-left-tagline">
                        <strong>Admin Dashboard</strong>
                        Manage your art collection platform with complete visibility and control.
                    </p>
                    <div className="login-features">
                        {features.map(({ icon, text }) => (
                            <div className="login-feature-item" key={text}>
                                <div className="login-feature-icon">
                                    <i className={icon} />
                                </div>
                                <span>{text}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Right panel */}
            <div className="login-right">
                <div className="login-card">
                    <div className="login-card-brand">
                        <div className="login-card-brand-dot" />
                        <span className="login-card-brand-name">Artfro Admin</span>
                    </div>

                    <div className="login-card-header">
                        <h1>Welcome back</h1>
                        <p>Sign in to your admin account to continue</p>
                    </div>

                    {apiError && (
                        <div className="login-alert">
                            <i className="fa-solid fa-circle-exclamation" />
                            {apiError}
                        </div>
                    )}

                    <form onSubmit={handleLoginSubmit}>
                        <div className="login-field">
                            <label htmlFor="email">Email address</label>
                            <div className="login-input-wrap">
                                <i className="fa-regular fa-envelope login-input-icon" />
                                <input
                                    id="email"
                                    type="email"
                                    name="email"
                                    value={userDetail.email}
                                    onChange={onValueChange}
                                    placeholder="you@example.com"
                                    className={`login-input${emailError ? ' is-invalid' : ''}`}
                                    autoComplete="email"
                                />
                            </div>
                            {emailError && <p className="login-error-text">{emailError}</p>}
                        </div>

                        <div className="login-field">
                            <label htmlFor="password">Password</label>
                            <div className="login-input-wrap">
                                <i className="fa-solid fa-lock login-input-icon" />
                                <input
                                    id="password"
                                    type="password"
                                    name="password"
                                    value={userDetail.password}
                                    onChange={onValueChange}
                                    placeholder="Enter your password"
                                    className={`login-input${passwordError ? ' is-invalid' : ''}`}
                                    autoComplete="current-password"
                                />
                            </div>
                            {passwordError && <p className="login-error-text">{passwordError}</p>}
                        </div>

                        <button type="submit" className="login-submit-btn" disabled={loading}>
                            {loading ? (
                                <><div className="login-spinner" /> Signing in...</>
                            ) : (
                                <>Sign in <i className="fa-solid fa-arrow-right" /></>
                            )}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default Login;
